const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value: number) => `${value}`.padStart(2, '0');

const parseDateInput = (value?: string | number | Date | null): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  // Unix millisecond timestamp sent as a numeric string (e.g. "1746482640000")
  if (/^\d{10,15}$/.test(raw)) {
    const d = new Date(Number(raw));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const match = raw.match(DATE_ONLY_PATTERN);
  if (match) {
    const next = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(next.getTime()) ? null : next;
  }

  const next = new Date(raw);
  return Number.isNaN(next.getTime()) ? null : next;
};

export const formatStandardDate = (value?: string | number | Date | null): string => {
  const date = parseDateInput(value);
  if (!date) return '—';
  return `${pad(date.getDate())}:${pad(date.getMonth() + 1)}:${date.getFullYear()}`;
};

export const formatStandardTime = (value?: string | number | Date | null): string => {
  const date = parseDateInput(value);
  if (!date) return '—';
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const formatStandardDateTime = (value?: string | number | Date | null): string => {
  const date = parseDateInput(value);
  if (!date) return '—';
  return `${formatStandardDate(date)} ${formatStandardTime(date)}`;
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const formatGmailDate = (value?: string | number | Date | null): string => {
  const date = parseDateInput(value);
  if (!date) return '—';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (sameDay) return `${pad(date.getHours())}:${pad(date.getMinutes())}`;

  if (diffDays === 1) return 'Yesterday';

  if (diffDays < 7) return DAYS_SHORT[date.getDay()];

  if (date.getFullYear() === now.getFullYear()) {
    return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`;
  }

  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
};

export const isSameCalendarDay = (
  left?: string | number | Date | null,
  right?: string | number | Date | null,
): boolean => {
  const leftDate = parseDateInput(left);
  const rightDate = parseDateInput(right);
  if (!leftDate || !rightDate) return false;
  return (
    leftDate.getFullYear() === rightDate.getFullYear() &&
    leftDate.getMonth() === rightDate.getMonth() &&
    leftDate.getDate() === rightDate.getDate()
  );
};
